import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useField } from "formik";
import JoditEditor from "jodit-react";
import { encodeHTML, decodeHTML } from "../util/generalUtil";
import "../styles/JoditScrollbar.css";
import InfoDescription from "./InfoDescription";

export const editorConfig = {
  minHeight: 200,
  maxHeight: 400,
  style: {
    minHeight: "200px",
    maxHeight: "400px",
    overflow: "auto",
  },
};

function ExtendedRichText(props) {
  const { name, label, validation, indent = "false", showLabel = true, description, } = props;
  const editor = useRef(null);
  const [field, , helpers] = useField(name);
  const { setValue, setTouched } = helpers;

  // 1) Normalize the incoming Formik value once, safely
  const raw = typeof field.value === "string" ? field.value : "";
  const safeFieldValue = useMemo(() => {
    try {
      // If it’s already encoded (e.g., contains &lt;), decode to show readable HTML
      return decodeHTML(raw);
    } catch {
      // Fallback: show as-is rather than crash
      return raw || "";
    }
  }, [raw]);

  // 2) Local state shown by Jodit (always readable HTML)
  const [content, setContent] = useState(safeFieldValue);

  // 3) Keep local state in sync if Formik value changes externally
  useEffect(() => {
    if (safeFieldValue !== content) {
      setContent(safeFieldValue);
    }
  }, [safeFieldValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = useCallback(
    (newContent) => {
      // Jodit gives HTML string
      const html = typeof newContent === "string" ? newContent : "";
      setContent(html);

      // Save the ENCODED version in Formik so it’s safe for DB transport
      const encoded = encodeHTML(html);
      if (encoded !== field.value) {
        setValue(encoded); // Formik stores escaped entities
        setTouched(true);
      }
    },
    ["newContent"],
  );

  const isIndentYes = (indent || "").toString().toLowerCase() === "yes";

  return (
    <>
      {label && showLabel && (
        <div className="flex space-x-2">
          <label
            className="text-sm font-normal inline-block text-custom-gray-400"
            htmlFor={name}
          >
            {label}{" "}
            {validation?.required == true && (
              <span className="text-sm text-asterisk-red">&#42;</span>
            )}
          </label>
          {description && (
            <InfoDescription text={description} setIcon={true} name={name} />
          )}
        </div>
      )}

      <div className="relative border border-gray-400 p-4 pb-9">
        <JoditEditor
          config={editorConfig}
          ref={editor}
          value={content} // readable HTML
          onChange={handleChange} // stores encoded value into Formik
        />
      </div>
    </>
  );
}

export default ExtendedRichText;
